"use client";

import { c as _c } from "react/compiler-runtime";
import { AccountLockup, AccountLockupAvatar, AccountLockupFragment, AccountLockupText } from "@opensea/app-kit";
import { graphql, readFragment } from "@opensea/graphql";
import { Link } from "@opensea/next-ui-kit";
import { Flex, FlexCenter, FlexColumn, NumberDisplay, Progress, TABLE_SIZES, TableCell, TableRow, TextBody, TextLabel, TextOverflowTooltip, tableRowSizeVariants, useTable } from "@opensea/ui-kit";
import { getProfileUrl, profileUrlFragment } from "@/components/account/utils";
import { ItemAvatar, ItemAvatarFragment } from "@/components/items/avatar/ItemAvatar";
import { ItemLink, ItemLinkFragment } from "@/components/items/ItemLink";
import { ItemPreviewTooltip } from "@/components/items/ItemPreviewTooltip";
import { ItemPreviewTooltipFragment } from "@/components/items/ItemPreviewTooltip/ItemPreviewTooltip.fragment";
import { TokenPrice } from "@/components/price/TokenPrice";
import { TokenPriceFragment } from "@/components/price/TokenPrice.fragment";
import { ProfilePreviewTooltip } from "@/components/profile/ProfilePreviewTooltip/ProfilePreviewTooltip";
import { ProfilePreviewTooltipFragment } from "@/components/profile/ProfilePreviewTooltip/ProfilePreviewTooltip.fragment";
import { useAddress } from "@/hooks/wallet";
import { CollectionHoldersTableSkeletonRow } from "./CollectionHoldersTableSkeletonRow";
import { COLLECTION_HOLDERS_TABLE_COLUMN_CLASSNAMES, COLLECTION_HOLDERS_TABLE_COMPACT_COLUMN_CLASSNAMES } from "./styles";
export const CollectionHoldersTableRowFragment = graphql(`
    fragment CollectionHoldersTableRow on CollectionTopOwner {
      quantity
      percent
      previewItems(limit: 5) {
        ...ItemLink
        ...ItemPreviewTooltip
        ...ItemAvatar
        tokenId
      }
      profile {
        displayName
        portfolioSummary {
          estimatedNftValue {
            usd
          }
        }
        ...profileUrl
        ...AccountLockup
        ...ProfilePreviewTooltip
      }
    }
  `, [profileUrlFragment, AccountLockupFragment, ProfilePreviewTooltipFragment, ItemLinkFragment, ItemPreviewTooltipFragment, ItemAvatarFragment]);
export function CollectionHoldersTableRow(t0) {
  const $ = _c(74);
  const {
    item: itemKey,
    context
  } = t0;
  const holder = readFragment(CollectionHoldersTableRowFragment, itemKey);
  const {
    size
  } = useTable();
  const COLUMN_CLASSNAMES = size === "md" ? COLLECTION_HOLDERS_TABLE_COMPACT_COLUMN_CLASSNAMES : COLLECTION_HOLDERS_TABLE_COLUMN_CLASSNAMES;
  const connectedAddress = useAddress();
  if (!holder) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <CollectionHoldersTableSkeletonRow />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const {
    profile,
    quantity,
    percent,
    previewItems
  } = holder;
  const {
    image
  } = tableRowSizeVariants({
    size
  });
  const topOfferPrice = readFragment(TokenPriceFragment, context?.topOfferPrice);
  const imageSize = TABLE_SIZES[size].image;
  const fillPercentage = quantity / (context?.topHoldersQuantity ?? 0) * 100;
  const portfolioPercent = topOfferPrice?.usd && holder.profile.portfolioSummary.estimatedNftValue.usd ? Math.min(topOfferPrice.usd * quantity / holder.profile.portfolioSummary.estimatedNftValue.usd, 1) : null;
  const T0 = Link;
  const t1 = getProfileUrl(profile);
  let t2;
  if ($[1] !== context) {
    t2 = context?.collectionSlug ? {
      collectionSlugs: context.collectionSlug
    } : undefined;
    $[1] = context;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      pathname: t1,
      query: t2
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = "unstyled";
  const T1 = TableRow;
  const t5 = true;
  const t6 = size === "2xl" ? 36 : imageSize;
  let t7;
  if ($[6] !== t6) {
    t7 = <AccountLockupAvatar size={t6} />;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = connectedAddress ?? null;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <TextOverflowTooltip><TextBody className="font-medium"><AccountLockupText /></TextBody></TextOverflowTooltip>;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== connectedAddress || $[10] !== profile) {
    t10 = profile.displayName ? <AccountLockup account={profile} addressDisplay="compact" className="text-text-secondary" connectedAddress={connectedAddress ?? null} showDisplayName={false} /> : null;
    $[9] = connectedAddress;
    $[10] = profile;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10) {
    t11 = <FlexColumn className="w-[calc(100%-60px)]">{t9}{t10}</FlexColumn>;
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== profile || $[15] !== t11 || $[16] !== t7 || $[17] !== t8) {
    t12 = <Flex className="w-full items-center gap-2"><AccountLockup account={profile} addressDisplay="compact" avatar={t7} className="w-full gap-3" connectedAddress={t8} disableTextOverflowTooltip={true} isLink={false}>{t11}</AccountLockup></Flex>;
    $[14] = profile;
    $[15] = t11;
    $[16] = t7;
    $[17] = t8;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== profile || $[20] !== t12) {
    t13 = <ProfilePreviewTooltip profile={profile}>{t12}</ProfilePreviewTooltip>;
    $[19] = profile;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== COLUMN_CLASSNAMES.wallet || $[23] !== t13) {
    t14 = <TableCell className={COLUMN_CLASSNAMES.wallet}>{t13}</TableCell>;
    $[22] = COLUMN_CLASSNAMES.wallet;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== quantity) {
    t15 = <Flex className="px-[8px] py-[9px]"><NumberDisplay display="full" value={quantity} /></Flex>;
    $[25] = quantity;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== fillPercentage || $[28] !== t15) {
    t16 = <Progress value={fillPercentage} variant="green">{t15}</Progress>;
    $[27] = fillPercentage;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== COLUMN_CLASSNAMES.owned || $[31] !== t16) {
    t17 = <TableCell className={COLUMN_CLASSNAMES.owned}>{t16}</TableCell>;
    $[30] = COLUMN_CLASSNAMES.owned;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      maximumFractionDigits: 2
    };
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== percent) {
    t19 = <NumberDisplay custom={t18} display="percent" value={percent} />;
    $[34] = percent;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== COLUMN_CLASSNAMES.percent || $[37] !== t19) {
    t20 = <TableCell className={COLUMN_CLASSNAMES.percent}>{t19}</TableCell>;
    $[36] = COLUMN_CLASSNAMES.percent;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  const T2 = TableCell;
  const t21 = COLUMN_CLASSNAMES.items;
  const T3 = FlexCenter;
  const t22 = "gap-2";
  const t23 = previewItems.map(previewItem => <ItemPreviewTooltip item={previewItem} key={previewItem.tokenId}><ItemLink item={previewItem}><ItemAvatar className={image()} item={previewItem} size={imageSize} /></ItemLink></ItemPreviewTooltip>);
  let t24;
  if ($[39] !== previewItems.length || $[40] !== quantity) {
    t24 = quantity > previewItems.length && <TextLabel className="ml-0.5" size="xs"><NumberDisplay display="quantity" prefix="+" value={quantity - previewItems.length} /></TextLabel>;
    $[39] = previewItems.length;
    $[40] = quantity;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== T3 || $[43] !== t23 || $[44] !== t24) {
    t25 = <T3 className={t22}>{t23}{t24}</T3>;
    $[42] = T3;
    $[43] = t23;
    $[44] = t24;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== COLUMN_CLASSNAMES.items || $[47] !== T2 || $[48] !== t25) {
    t26 = <T2 className={t21}>{t25}</T2>;
    $[46] = COLUMN_CLASSNAMES.items;
    $[47] = T2;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  const t27 = context?.topOfferPrice;
  let t28;
  if ($[50] !== quantity || $[51] !== t27) {
    t28 = <TokenPrice multiplier={quantity} price={t27} symbolColor="current" />;
    $[50] = quantity;
    $[51] = t27;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== COLUMN_CLASSNAMES.value || $[54] !== t28) {
    t29 = <TableCell className={COLUMN_CLASSNAMES.value}>{t28}</TableCell>;
    $[53] = COLUMN_CLASSNAMES.value;
    $[54] = t28;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      maximumFractionDigits: 2
    };
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  let t31;
  if ($[57] !== portfolioPercent) {
    t31 = <NumberDisplay custom={t30} display="percent" value={portfolioPercent} />;
    $[57] = portfolioPercent;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== COLUMN_CLASSNAMES.portfolio || $[60] !== t31) {
    t32 = <TableCell className={COLUMN_CLASSNAMES.portfolio}>{t31}</TableCell>;
    $[59] = COLUMN_CLASSNAMES.portfolio;
    $[60] = t31;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] !== T1 || $[63] !== t14 || $[64] !== t17 || $[65] !== t20 || $[66] !== t26 || $[67] !== t29 || $[68] !== t32) {
    t33 = <T1 interactive={t5}>{t14}{t17}{t20}{t26}{t29}{t32}</T1>;
    $[62] = T1;
    $[63] = t14;
    $[64] = t17;
    $[65] = t20;
    $[66] = t26;
    $[67] = t29;
    $[68] = t32;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== T0 || $[71] !== t3 || $[72] !== t33) {
    t34 = <T0 href={t3} variant={t4}>{t33}</T0>;
    $[70] = T0;
    $[71] = t3;
    $[72] = t33;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  return t34;
}